import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Application {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  status: string;
  created_at: string;
  volunteer_opportunities: { title_en: string } | null;
}

interface Opportunity {
  id: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  description_en: string;
  description_ps: string;
  description_da: string;
  commitment: string;
  location: string;
  category: string;
  is_active: boolean;
  created_at: string;
}

interface Testimonial {
  id: string;
  name_en: string;
  name_ps: string;
  name_da: string;
  role_en: string;
  role_ps: string;
  role_da: string;
  quote_en: string;
  quote_ps: string;
  quote_da: string;
  duration: string;
  avatar_url: string;
  is_active: boolean;
  sort_order: number;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STATUS_OPTIONS = ['pending', 'reviewed', 'accepted', 'rejected'];
const TABS = ['Applications', 'Opportunities', 'Testimonials'] as const;
type Tab = typeof TABS[number];

const emptyOpportunity: Omit<Opportunity, 'id' | 'created_at'> = {
  title_en: '', title_ps: '', title_da: '',
  description_en: '', description_ps: '', description_da: '',
  commitment: '', location: '', category: '', is_active: true,
};

const emptyTestimonial: Omit<Testimonial, 'id'> = {
  name_en: '', name_ps: '', name_da: '',
  role_en: '', role_ps: '', role_da: '',
  quote_en: '', quote_ps: '', quote_da: '',
  duration: '', avatar_url: '', is_active: true, sort_order: 0,
};

/* ------------------------------------------------------------------ */
/*  Modals                                                             */
/* ------------------------------------------------------------------ */

function OpportunityModal({
  opp,
  onClose,
  onSaved,
}: {
  opp: Opportunity | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(opp ?? { ...emptyOpportunity, id: '', created_at: '' } as Opportunity);
  const [saving, setSaving] = useState(false);
  const isEdit = !!opp;

  const handleSave = async () => {
    setSaving(true);
    const { id, created_at, ...payload } = form;
    if (isEdit) {
      await supabase.from('volunteer_opportunities').update(payload).eq('id', id);
    } else {
      await supabase.from('volunteer_opportunities').insert(payload);
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-[#1B2838] text-lg">{isEdit ? 'Edit Opportunity' : 'Add Opportunity'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 cursor-pointer hover:bg-gray-200"><i className="ri-close-line"></i></button>
        </div>
        <div className="space-y-4">
          {(['en', 'ps', 'da'] as const).map(lang => (
            <div key={lang} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                placeholder={`Title (${lang.toUpperCase()})`}
                value={(form as any)[`title_${lang}`]}
                onChange={e => setForm({ ...form, [`title_${lang}`]: e.target.value })}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5]"
              />
              <input
                placeholder={`Description (${lang.toUpperCase()})`}
                value={(form as any)[`description_${lang}`]}
                onChange={e => setForm({ ...form, [`description_${lang}`]: e.target.value })}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5]"
              />
            </div>
          ))}
          <div className="grid grid-cols-3 gap-3">
            <input placeholder="Commitment" value={form.commitment} onChange={e => setForm({ ...form, commitment: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5]" />
            <input placeholder="Location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5]" />
            <input placeholder="Category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5]" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="accent-[#3B9EF5]" />
            Active
          </label>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-full text-sm font-medium text-gray-500 cursor-pointer hover:bg-gray-50 whitespace-nowrap">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function TestimonialModal({
  tItem,
  onClose,
  onSaved,
}: {
  tItem: Testimonial | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(tItem ?? { ...emptyTestimonial, id: '' } as Testimonial);
  const [saving, setSaving] = useState(false);
  const isEdit = !!tItem;

  const handleSave = async () => {
    setSaving(true);
    const { id, ...payload } = form;
    if (isEdit) {
      await supabase.from('volunteer_testimonials').update(payload).eq('id', id);
    } else {
      await supabase.from('volunteer_testimonials').insert(payload);
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-[#1B2838] text-lg">{isEdit ? 'Edit Testimonial' : 'Add Testimonial'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 cursor-pointer hover:bg-gray-200"><i className="ri-close-line"></i></button>
        </div>
        <div className="space-y-4">
          {(['en', 'ps', 'da'] as const).map(lang => (
            <div key={lang} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                placeholder={`Name (${lang.toUpperCase()})`}
                value={(form as any)[`name_${lang}`]}
                onChange={e => setForm({ ...form, [`name_${lang}`]: e.target.value })}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5]"
              />
              <input
                placeholder={`Role (${lang.toUpperCase()})`}
                value={(form as any)[`role_${lang}`]}
                onChange={e => setForm({ ...form, [`role_${lang}`]: e.target.value })}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5]"
              />
              <textarea
                placeholder={`Quote (${lang.toUpperCase()})`}
                value={(form as any)[`quote_${lang}`]}
                onChange={e => setForm({ ...form, [`quote_${lang}`]: e.target.value })}
                rows={2}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5] resize-none"
              />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Duration (e.g. 2 years)" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5]" />
            <input placeholder="Avatar URL" value={form.avatar_url} onChange={e => setForm({ ...form, avatar_url: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#3B9EF5]" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="accent-[#3B9EF5]" />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Sort:
              <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: Number(e.target.value) })} className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-[#3B9EF5]" />
            </label>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-full text-sm font-medium text-gray-500 cursor-pointer hover:bg-gray-50 whitespace-nowrap">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab: Applications                                                  */
/* ------------------------------------------------------------------ */

function ApplicationsTab() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selected, setSelected] = useState<Application | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('volunteer_applications').select('*, volunteer_opportunities(title_en)').order('created_at', { ascending: false });
    if (filter !== 'all') query = query.eq('status', filter);
    const { data } = await query;
    setApplications((data as Application[]) ?? []);
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateStatus = async (id: string, status: string) => {
    await supabase.from('volunteer_applications').update({ status }).eq('id', id);
    setApplications(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    if (selected?.id === id) setSelected(prev => prev ? { ...prev, status } : null);
  };

  const statusColor = (s: string) => {
    const map: Record<string, string> = { pending: 'bg-amber-50 text-amber-600', reviewed: 'bg-[#3B9EF5]/10 text-[#3B9EF5]', accepted: 'bg-green-100 text-green-700', rejected: 'bg-red-50 text-red-500' };
    return map[s] ?? 'bg-gray-100 text-gray-500';
  };

  return (
    <>
      <div className="flex gap-2 mb-5">
        {['all', ...STATUS_OPTIONS].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer capitalize transition-colors whitespace-nowrap ${filter === f ? 'bg-[#1B2838] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}>{f}</button>
        ))}
      </div>

      <div className="flex gap-5">
        <div className="flex-1 bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400"><div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>Loading...</div>
          ) : applications.length === 0 ? (
            <div className="p-12 text-center text-gray-400"><div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-team-line text-3xl"></i></div><p className="text-sm">No applications found</p></div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Applicant</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {applications.map(app => (
                  <tr key={app.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelected(app)}>
                    <td className="px-5 py-4"><div className="text-sm font-medium text-[#1B2838]">{app.name}</div><div className="text-xs text-gray-400">{app.email}</div></td>
                    <td className="px-5 py-4 text-sm text-gray-600">{app.volunteer_opportunities?.title_en ?? '\u2014'}</td>
                    <td className="px-5 py-4"><span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColor(app.status)}`}>{app.status}</span></td>
                    <td className="px-5 py-4 text-sm text-gray-400">{new Date(app.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4">
                      <select value={app.status} onChange={e => { e.stopPropagation(); updateStatus(app.id, e.target.value); }} onClick={e => e.stopPropagation()} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-[#3B9EF5] cursor-pointer capitalize">
                        {STATUS_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <div className="w-80 bg-white rounded-xl border border-gray-100 p-6 flex-shrink-0">
            <div className="flex items-start justify-between mb-4"><h3 className="font-bold text-[#1B2838]">{selected.name}</h3><button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-400 text-sm"></i></button></div>
            <div className="space-y-2 text-sm mb-4">
              <div className="flex gap-2"><span className="text-gray-400 w-16 flex-shrink-0">Email</span><a href={`mailto:${selected.email}`} className="text-[#3B9EF5] hover:underline truncate">{selected.email}</a></div>
              {selected.phone && <div className="flex gap-2"><span className="text-gray-400 w-16 flex-shrink-0">Phone</span><span className="text-gray-700">{selected.phone}</span></div>}
              <div className="flex gap-2"><span className="text-gray-400 w-16 flex-shrink-0">Role</span><span className="text-gray-700">{selected.volunteer_opportunities?.title_en ?? '\u2014'}</span></div>
            </div>
            {selected.message && <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 leading-relaxed mb-4">{selected.message}</div>}
            <div className="space-y-2">
              {STATUS_OPTIONS.map(s => (
                <button key={s} onClick={() => updateStatus(selected.id, s)} disabled={selected.status === s} className={`w-full py-2 rounded-full text-xs font-medium cursor-pointer capitalize transition-colors whitespace-nowrap ${selected.status === s ? 'bg-[#1B2838] text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'} disabled:cursor-default`}>{s}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab: Opportunities                                                  */
/* ------------------------------------------------------------------ */

function OpportunitiesTab() {
  const [items, setItems] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Opportunity | null>(null);
  const [adding, setAdding] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('volunteer_opportunities').select('*').order('created_at', { ascending: false });
    setItems((data as Opportunity[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleActive = async (id: string, current: boolean) => {
    await supabase.from('volunteer_opportunities').update({ is_active: !current }).eq('id', id);
    setItems(prev => prev.map(i => i.id === id ? { ...i, is_active: !current } : i));
  };

  const deleteItem = async (id: string) => {
    if (!confirm('Delete this opportunity? Applications referencing it will lose their role link.')) return;
    await supabase.from('volunteer_opportunities').delete().eq('id', id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  return (
    <>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{items.length} opportunit{items.length === 1 ? 'y' : 'ies'}</p>
        <button onClick={() => setAdding(true)} className="px-4 py-2 bg-[#1B2838] text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-[#2a3a4a] whitespace-nowrap"><i className="ri-add-line mr-1"></i> Add Opportunity</button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-400"><div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>Loading...</div>
      ) : items.length === 0 ? (
        <div className="p-12 text-center text-gray-400"><div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-briefcase-line text-3xl"></i></div><p className="text-sm">No opportunities yet</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Title (EN)</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Commitment</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Active</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3 text-sm font-medium text-[#1B2838] max-w-[240px] truncate">{item.title_en || '\u2014'}</td>
                  <td className="px-5 py-3 text-sm text-gray-500">{item.category || '\u2014'}</td>
                  <td className="px-5 py-3 text-sm text-gray-500">{item.location || '\u2014'}</td>
                  <td className="px-5 py-3 text-sm text-gray-500">{item.commitment || '\u2014'}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggleActive(item.id, item.is_active)} className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer ${item.is_active ? 'bg-[#3B9EF5]' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${item.is_active ? 'left-5' : 'left-0.5'}`}></span>
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(item)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-400 hover:text-[#1B2838]" title="Edit"><i className="ri-edit-line text-sm"></i></button>
                      <button onClick={() => deleteItem(item.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer text-gray-400 hover:text-red-500" title="Delete"><i className="ri-delete-bin-line text-sm"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(adding || editing) && (
        <OpportunityModal opp={editing} onClose={() => { setAdding(false); setEditing(null); }} onSaved={fetch} />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab: Testimonials                                                   */
/* ------------------------------------------------------------------ */

function TestimonialsTab() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Testimonial | null>(null);
  const [adding, setAdding] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('volunteer_testimonials').select('*').order('sort_order', { ascending: true });
    setItems((data as Testimonial[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleActive = async (id: string, current: boolean) => {
    await supabase.from('volunteer_testimonials').update({ is_active: !current }).eq('id', id);
    setItems(prev => prev.map(i => i.id === id ? { ...i, is_active: !current } : i));
  };

  const deleteItem = async (id: string) => {
    if (!confirm('Delete this testimonial?')) return;
    await supabase.from('volunteer_testimonials').delete().eq('id', id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  return (
    <>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{items.length} testimonial{items.length === 1 ? '' : 's'}</p>
        <button onClick={() => setAdding(true)} className="px-4 py-2 bg-[#1B2838] text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-[#2a3a4a] whitespace-nowrap"><i className="ri-add-line mr-1"></i> Add Testimonial</button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-400"><div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>Loading...</div>
      ) : items.length === 0 ? (
        <div className="p-12 text-center text-gray-400"><div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-double-quotes-l text-3xl"></i></div><p className="text-sm">No testimonials yet</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {item.avatar_url ? (
                    <img src={item.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400"><i className="ri-user-line"></i></div>
                  )}
                  <div>
                    <div className="font-semibold text-[#1B2838] text-sm">{item.name_en || '\u2014'}</div>
                    <div className="text-xs text-gray-400">{item.role_en || '\u2014'}</div>
                  </div>
                </div>
                <button onClick={() => toggleActive(item.id, item.is_active)} className={`w-9 h-5 rounded-full relative transition-colors cursor-pointer flex-shrink-0 ${item.is_active ? 'bg-[#3B9EF5]' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${item.is_active ? 'left-4' : 'left-0.5'}`}></span>
                </button>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-3 italic">&ldquo;{item.quote_en || '\u2014'}&rdquo;</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{item.duration || '\u2014'}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(item)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-400 hover:text-[#1B2838]" title="Edit"><i className="ri-edit-line text-xs"></i></button>
                  <button onClick={() => deleteItem(item.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer text-gray-400 hover:text-red-500" title="Delete"><i className="ri-delete-bin-line text-xs"></i></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(adding || editing) && (
        <TestimonialModal tItem={editing} onClose={() => { setAdding(false); setEditing(null); }} onSaved={fetch} />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

export default function AdminVolunteersPage() {
  const [tab, setTab] = useState<Tab>('Applications');

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1B2838]">Volunteer Dashboard</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage applications, opportunities, and testimonials</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/volunteer"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-full text-sm font-medium cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-eye-line"></i>
              Preview Page
            </a>
            <a
              href="/admin/content/english"
              className="px-4 py-2 bg-[#3B9EF5]/10 text-[#3B9EF5] rounded-full text-sm font-medium cursor-pointer hover:bg-[#3B9EF5]/20 transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-edit-2-line"></i>
              Edit CMS Content
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-full p-1 mb-6 w-fit">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${tab === t ? 'bg-white text-[#1B2838]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'Applications' && <ApplicationsTab />}
        {tab === 'Opportunities' && <OpportunitiesTab />}
        {tab === 'Testimonials' && <TestimonialsTab />}
      </main>
    </div>
  );
}